import React from 'react';
import { TextAreaInterface } from './types';
import propTypes from './prop_types';
import mergeRefs from 'react-merge-refs';
import useStyles from './use_styles';
import Scrollbar from '../scrollbar';
import HelperText from '../shared/form/helper_text';
import Label from '../shared/form/label';
import { useTheme } from '@veneer/theme';
import useInputStyles from '../shared/form/use_input_styles';
import useInputId from '../shared/form/use_input_id';
import useDirection from '../direction/use_direction';

const TextArea = React.forwardRef<
  HTMLTextAreaElement,
  TextAreaInterface &
    Omit<
      React.InputHTMLAttributes<HTMLTextAreaElement>,
      keyof TextAreaInterface
    >
>(
  (
    {
      autoResize = false,
      className,
      disabled = false,
      disableLabelShrinking = false,
      error = false,
      height = '120px',
      helperText,
      helperTextState = 'default',
      helperTextVisibility = 'auto',
      id: idProp,
      label,
      maxHeight,
      noTruncate = false,
      onChange,
      onHeightChange,
      placeholder,
      readOnly = false,
      required = false,
      resize: resizeProp = false,
      separateLabel = false,
      maxLength,
      counter,
      countCallback,
      ...rest
    },
    ref
  ) => {
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    const handleRefs = mergeRefs([textAreaRef, ref]);
    const { id, helperTextId, labelId } = useInputId('vn-textarea', idProp);

    const theme = useTheme();
    const isHd = theme.density === 'high';
    const isHc = theme.mode === 'contrast';
    const isPill = theme.shape === 'pill';
    const { isRTL } = useDirection();
    const resize =
      resizeProp === true ? 'both' : resizeProp === false ? 'none' : resizeProp;

    const insideLabel = !separateLabel && !isHd;

    const [dimensions, setDimensions] = React.useState({
      width: 'auto',
      height,
    });

    React.useEffect(() => {
      const textArea = textAreaRef.current;
      if (!textArea) return;

      const handleResize = () => {
        setDimensions({
          width: `${textArea.offsetWidth}px`,
          height: `${textArea.offsetHeight}px`,
        });
      };

      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(textArea);

      handleResize();

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    const style = useStyles({
      width: dimensions.width,
      height: height ?? dimensions.height,
      resize,
      maxHeight,
    });
    const containerStyle = useInputStyles({});

    const heightOffset = 2;
    const handleAutoResize = React.useCallback(
      (e: HTMLTextAreaElement) => {
        const originalHeight = parseFloat(e.style.height);
        e.style.height = 'auto';
        let newHeight = e.scrollHeight + heightOffset;

        if (newHeight < parseFloat(height)) newHeight = parseFloat(height);
        e.style.height = `${newHeight}px`;
        if (originalHeight !== newHeight) {
          onHeightChange?.(`${newHeight}px`, e);
        }
      },
      [height, onHeightChange]
    );

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (autoResize) {
          handleAutoResize(event.target);
        }
        onChange?.(event.target.value, event);
      },
      [autoResize, handleAutoResize, onChange]
    );

    return (
      <div
        className={className}
        css={[
          containerStyle.root.base,
          helperTextVisibility === 'auto' &&
            containerStyle.root.helperTextVisible,
        ]}
      >
        {label && !insideLabel && (
          <div css={resizeProp && style.container.resizable}>
            <Label
              htmlFor={id}
              disabled={disabled}
              id={labelId}
              required={required ?? false}
              truncate={!noTruncate}
              counter={counter}
              counterLimit={maxLength}
              countCallback={countCallback}
              inputRef={textAreaRef}
            >
              {label}
            </Label>
          </div>
        )}

        <div
          css={[style.container.base, resizeProp && style.container.resizable]}
        >
          {label && insideLabel && (
            <Label
              htmlFor={id}
              applyTo="textarea"
              disabled={disabled}
              disableLabelShrinking={disableLabelShrinking}
              id={labelId}
              inputRef={textAreaRef}
              inside
              required={required ?? false}
              counter={counter}
              counterLimit={maxLength}
              countCallback={countCallback}
            >
              {label}
            </Label>
          )}

          <Scrollbar
            tabIndex={null}
            css={[
              containerStyle.input.base,
              isHc && containerStyle.input.hc,
              style.textarea.base,
              isPill && style.textarea.pill,
              insideLabel && [
                style.textarea.insideLabel,
                isPill && style.textarea.pillAndInside,
              ],
            ]}
            {...rest}
            aria-describedby={helperTextId}
            aria-invalid={error}
            required={required}
            onChange={handleChange}
            placeholder={placeholder ?? ' '}
            ref={handleRefs}
            component="textarea"
            disabled={disabled}
            id={id}
            readOnly={readOnly}
            maxLength={maxLength}
            {...rest}
          />

          {resize !== 'none' && (
            <span
              css={[style.resizer.base, isRTL && style.resizer.rtl]}
              aria-hidden
            >
              <span css={style.resizer.biggerLine} />
              <span css={style.resizer.smallerLine} />
            </span>
          )}
        </div>

        {helperText && (
          <HelperText
            disabled={disabled}
            error={error}
            helperTextState={helperTextState}
            helperTextVisibility={helperTextVisibility}
            id={helperTextId}
          >
            {helperText}
          </HelperText>
        )}
      </div>
    );
  }
);

TextArea.propTypes = propTypes;

TextArea.displayName = 'TextArea';

export default TextArea;
